import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DunningService} from '../../../service/api/dunning.service';
import {BdDunningDto} from '../../../model/dto/api/bd-dunning.dto';
import {BdManualContractDecisionDto} from '../../../model/dto/api/bd-manual-contract-decision.dto';
import {extractError} from '../../../util/error-extraction.util';

/**
 * Erlaubte Eskalations-Typen.
 */
type BdEscalationType =
    | 'INKASSO_HANDOVER'
    | 'LAWYER'
    | 'WRITE_OFF'
    | 'SPECIAL_HANDLING'
    | 'OTHER';

/**
 * Auswahl-Option fuer den Eskalations-Typ-Dropdown.
 */
interface BdEscalationTypeOption {
    label: string;
    value: BdEscalationType;
}

/**
 * AI:
 * Status: 'generated'
 * Type: Component
 * SubType: Exit-UI / Escalation-Dialog
 * Reason: Pending-Action-Dialog fuer die Eskalation einer Mahnung. Erfasst den
 *         Eskalations-Typ (Pflicht) und eine Pflicht-Begruendung. Mapped jeden
 *         Typ auf die passende manuelle Vertragsentscheidung:
 *           - Inkasso-Uebergabe / Rechtsanwalt / Sonderbehandlung / Sonstige -> HANDOVER
 *           - Forderung abschreiben                                          -> WRITE_OFF
 *         Der Eskalations-Typ wird im Comment-Feld in eckigen Klammern voran-
 *         gestellt, damit das Backend differenzieren kann.
 *
 *         Mapped to {@link BdDunningExitUiComponent.ESCALATION_DIALOG}.
 *
 * @ticket TM-1000001782
 */
@Component({
    selector: 'mvs-bd-escalation-dialog',
    templateUrl: './bd-escalation-dialog.component.html',
    styleUrls: ['./bd-escalation-dialog.component.scss'],
    standalone: false
})
export class BdEscalationDialogComponent implements OnInit {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    @Input() dunning: BdDunningDto | null = null;
    @Input() uiComponentConfig: string | null = null;

    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Felder. Nicht
    // direkt verwendet, aber als @Input() deklariert, damit die Drawer-Bindung
    // kein NG0303 auslöst.
    @Input() dunningId: number | null = null;
    @Input() contracts: unknown[] | null = null;
    @Input() handOvers: unknown[] | null = null;
    @Input() activePause: unknown | null = null;
    @Input() breachedPlan: unknown | null = null;
    @Input() currentUserId: number | null = null;

    // ------------------------------------------------------------------ //
    // Outputs
    // ------------------------------------------------------------------ //

    @Output() actionCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() actionCancelled: EventEmitter<void> = new EventEmitter<void>();

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    visible: boolean = true;
    submitting: boolean = false;
    errorMessage: string | null = null;

    /** Anzeige-Felder fuer den Mahnungs-Header. */
    customerName: string = '—';
    dunningTypeName: string = '—';
    activeLevelLabel: string = '—';

    /** Vom Benutzer ausgewaehlter Eskalations-Typ. */
    selectedType: BdEscalationType | null = null;

    /** Pflicht-Begruendung. */
    comment: string = '';

    /** Auswahl-Optionen fuer den Eskalations-Typ. */
    typeOptions: BdEscalationTypeOption[] = [
        {label: 'Inkasso-Übergabe',         value: 'INKASSO_HANDOVER'},
        {label: 'Rechtsanwalt einschalten', value: 'LAWYER'},
        {label: 'Forderung abschreiben',    value: 'WRITE_OFF'},
        {label: 'Sonderbehandlung',         value: 'SPECIAL_HANDLING'},
        {label: 'Sonstige',                 value: 'OTHER'}
    ];

    constructor(private readonly dunningService: DunningService) {
    }

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.initComponent();
    }

    private initComponent(): void {
        this.customerName = this.buildCustomerName(this.dunning);
        this.dunningTypeName = this.readString(this.dunning, ['dunningTypeName', 'typeName']) ?? '—';
        this.activeLevelLabel = this.readString(this.dunning, ['activeLevelShortName', 'activeLevelName']) ?? '—';
        this.initialized = true;
    }

    // ------------------------------------------------------------------ //
    // Actions
    // ------------------------------------------------------------------ //

    /**
     * Sendet die Eskalations-Entscheidung ans Backend.
     */
    confirm(): void {
        if (this.selectedType == null) {
            this.errorMessage = 'Bitte einen Eskalations-Typ auswählen.';
            return;
        }
        const trimmedComment = this.comment?.trim() ?? '';
        if (trimmedComment.length === 0) {
            this.errorMessage = 'Bitte eine Begründung angeben.';
            return;
        }

        const dunningId = this.resolveDunningId();
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnungs-ID verfügbar.';
            return;
        }

        const action = this.mapTypeToAction(this.selectedType);
        const decision: BdManualContractDecisionDto = {
            action,
            comment: `[${this.selectedType}] ${trimmedComment}`
        };

        this.errorMessage = null;
        this.submitting = true;

        this.dunningService.decideManualContractDecision(dunningId, decision).subscribe({
            next: () => {
                this.submitting = false;
                this.closeDialog();
                this.actionCompleted.emit();
            },
            error: (err: unknown) => {
                this.submitting = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    onCancel(): void {
        this.closeDialog();
        this.actionCancelled.emit();
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.actionCancelled.emit();
        }
    }

    // ------------------------------------------------------------------ //
    // Helpers
    // ------------------------------------------------------------------ //

    /**
     * Mappt den UI-seitig gewaehlten Eskalations-Typ auf die manuelle Vertragsentscheidung.
     * Abschreibung -> WRITE_OFF, alles andere -> HANDOVER (Backend kann ueber
     * das Comment-Feld weiter differenzieren).
     */
    private mapTypeToAction(type: BdEscalationType): 'HANDOVER' | 'WRITE_OFF' {
        if (type === 'WRITE_OFF') {
            return 'WRITE_OFF';
        }
        return 'HANDOVER';
    }

    private closeDialog(): void {
        this.visible = false;
    }

    private resolveDunningId(): number | null {
        if (!this.dunning) {
            return null;
        }
        const id = (this.dunning.id ?? this.dunning['dunningId']) as unknown;
        return typeof id === 'number' && id > 0 ? id : null;
    }

    private buildCustomerName(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const first = (dunning.personFirstName ?? dunning.firstName ?? '') as string;
        const last = (dunning.personLastName ?? dunning.lastName ?? '') as string;
        const combined = `${first} ${last}`.trim();
        return combined.length > 0 ? combined : '—';
    }

    private readString(source: BdDunningDto | null, keys: string[]): string | null {
        if (!source) {
            return null;
        }
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return null;
    }
}
